import type { Gallery } from '@/lib/photos'

export const SPORT_KEYWORDS = [
  'wrestling',
  'flag football',
  'football',
  'volleyball',
  'basketball',
  'soccer',
  'baseball',
  'softball',
  'lacrosse',
  'track and field',
  'cross country',
  'cheer',
  'dance',
  'water polo',
  'swimming',
  'tennis',
  'golf',
]

const BLOCKED_SEGMENTS = ['event', 'events', 'gallery', 'galleries', 'album', 'albums', 'photos']

const isYear = (value: string) => /^\d{4}$/.test(value)
const isSchoolYear = (value: string) => /^\d{2}-\d{2}$/.test(value)

export const normalizeWord = (value: string) => value.replace(/[-_]+/g, ' ').trim()

export const titleCase = (value: string) => value.replace(/\w\S*/g, (txt) => txt[0].toUpperCase() + txt.slice(1))

export function deriveSport(gallery: Gallery) {
  const candidates = [
    ...gallery.keywords,
    ...gallery.folderName.split('/'),
  ]
    .map(normalizeWord)
    .filter(Boolean)
    .filter(candidate => {
      const lower = candidate.toLowerCase()
      return !BLOCKED_SEGMENTS.includes(lower) && !isYear(candidate)
    })

  for (const sport of SPORT_KEYWORDS) {
    const lowerSport = sport.toLowerCase()
    const hit = candidates.find(candidate => candidate.toLowerCase().includes(lowerSport))
    if (hit) return titleCase(sport)
  }

  return null
}

export function deriveSchoolYear(gallery: Gallery) {
  const parts = gallery.folderName.split('/')
  const folderYear = parts.find(isSchoolYear)
  return folderYear || null
}

export function isSportsIndexGallery(gallery: Gallery) {
  const parts = gallery.folderName.split('/')
  return parts.length === 3 && isSchoolYear(parts[1]) && !isTravelGallery(gallery)
}

export function isTravelGallery(gallery: Gallery) {
  const haystack = `${gallery.type || ''} ${gallery.title} ${gallery.folderName} ${gallery.keywords.join(' ')}`.toLowerCase()
  return gallery.type === 'portfolio' || haystack.includes('travel') || haystack.includes('landscape')
}

export function deriveTravelCollection(gallery: Gallery) {
  const folderParts = gallery.folderName.split('/').map(normalizeWord).filter(Boolean)
  const travelIndex = folderParts.findIndex(part => part.toLowerCase() === 'travel')
  const folderCandidate = travelIndex >= 0 ? folderParts[travelIndex + 1] : folderParts[0]
  const candidate = folderCandidate || gallery.location || gallery.title

  if (!candidate) return 'Travel Albums'

  return titleCase(candidate)
}

export function getSportsNavItems(galleries: Gallery[]) {
  const map = new Map<string, { name: string; count: number; years: Map<string, { name: string; count: number }> }>()
  let total = 0

  galleries.forEach(gallery => {
    if (gallery.type === 'event' && gallery.count === 0) return
    if (!isSportsIndexGallery(gallery)) return

    const sport = deriveSport(gallery)
    if (!sport) return

    const year = deriveSchoolYear(gallery)
    if (!year) return

    total += 1
    const key = sport.toLowerCase()
    if (!map.has(key)) {
      map.set(key, { name: sport, count: 0, years: new Map() })
    }
    const current = map.get(key)
    if (current) {
      current.count += 1
      if (!current.years.has(year)) {
        current.years.set(year, { name: year, count: 0 })
      }
      const currentYear = current.years.get(year)
      if (currentYear) currentYear.count += 1
    }
  })

  return {
    total,
    items: Array.from(map.values())
      .map((sport) => ({
        name: sport.name,
        count: sport.count,
        years: Array.from(sport.years.values()).sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true })),
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  }
}

export function getTravelNavItems(galleries: Gallery[]) {
  const map = new Map<string, { name: string; count: number; galleries: Gallery[] }>()
  let total = 0

  galleries.forEach(gallery => {
    if (gallery.type === 'event' || !isTravelGallery(gallery)) return

    total += 1
    const collection = deriveTravelCollection(gallery)
    const key = collection.toLowerCase()
    if (!map.has(key)) {
      map.set(key, { name: collection, count: 0, galleries: [] })
    }
    const current = map.get(key)
    if (current) {
      current.count += 1
      current.galleries.push(gallery)
    }
  })

  return {
    total,
    items: Array.from(map.values())
      .map((item) => {
        const directGallery = item.count === 1 ? item.galleries[0] : undefined
        return {
          name: directGallery?.title || item.name,
          collectionName: item.name,
          count: item.count,
          directGalleryId: directGallery?.id,
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
  }
}
